import React, { useState, useEffect } from 'react';
import { Box, Typography, FormControlLabel, Switch } from '@mui/material';
import { PopupDialog, TextFieldComponent } from '../../../../components/common';
import { AsociacionSelect } from '../../../catalogo/asociacion/components/AsociacionSelect';
import { EspecieSelect } from '../../../catalogo/especies/components/EspecieSelect';
import { type DERCEditDialogProps, type DERCRequest } from '../types/derc';
import { useDERCForm } from '../hooks/useDERCForm';
import { http } from '../../../../api/http';
import { useAsociaciones } from '../../../catalogo/asociacion/store/asociacionStore';
import DocumentNumberField from './DocumentNumberField';
import DocumentNumberFieldB from './DocumentNumberFieldB';

// Helper function to convert API response to DERCRequest
const createConvertToDERCRequest = (asociaciones: any[]) => (apiResponse: any): DERCRequest => {
  // Find asociacion ID by description
  const asociacion = asociaciones.find(a => a.descripcion === apiResponse.asociacionDescripcion);

  return {
    id: apiResponse.id,
    fechaExtraccion: apiResponse.fechaExtraccion ? apiResponse.fechaExtraccion.split('T')[0] : '', // Convert to YYYY-MM-DD format
    numeroDocumento: apiResponse.numeroDocumento || '',
    idOrigenProducto: apiResponse.idOrigenProducto || '',
    numeroMallas: apiResponse.numeroMallas || 0,
    numeroManojos: apiResponse.numeroManojos || 0,
    placaVehiculo: apiResponse.placaVehiculo || '',
    idAsociacion: asociacion?.idAsociacion || '',
    observacion: apiResponse.observaciones || '',
    idEspecie: apiResponse.idEspecie || '',
    isEntero: apiResponse.isEntero || false,
  };
};

const DERC_CONSTANTS = {
  DIALOG_CONFIG: {
    DEFAULT_TITLE: 'DERC',
    MAX_WIDTH: 'md' as const,
  },
  FIELD_LABELS: {
    fechaExtraccion: 'Fecha de Extracción',
    numeroDocumento: 'Número de Documento',
    idOrigenProducto: 'Origen del Producto',
    numeroMallas: 'Número de Mallas',
    numeroManojos: 'Número de Manojos',
    placaVehiculo: 'Placa del Vehículo',
    idAsociacion: 'Asociación',
    observacion: 'Observación',
    idEspecie: 'Especie',
    isEntero: 'Entero',
  },
};

/**
 * EditDERC Component
 *
 * A comprehensive DERC editing dialog that combines form fields and dialog functionality.
 */
const EditDERC: React.FC<DERCEditDialogProps> = ({
  open,
  dercId,
  onClose,
  onSave,
  title = DERC_CONSTANTS.DIALOG_CONFIG.DEFAULT_TITLE,
  defaultIdOrigenProducto,
}) => {
  const [initialDERCRequest, setInitialDERCRequest] = useState<DERCRequest | null>(null);
  const [loading, setLoading] = useState(false);

  // Get asociaciones for ID lookup
  const asociaciones = useAsociaciones();

  const {
    formData,
    errors,
    isSubmitting,
    isDirty,
    handleFieldChange,
    handleSubmit,
    resetForm,
  } = useDERCForm({
    initialDERCRequest,
    isOpen: open,
    onSubmit: onSave,
    defaultIdOrigenProducto,
  });


  // Fetch DERC data when editing
  useEffect(() => {
    const fetchDERCData = async () => {
      if (open && dercId) {
        setLoading(true);
        try {
          const response = await http.get(`/documento/${dercId}`);
          const convertToDERCRequest = createConvertToDERCRequest(asociaciones);
          const dercRequest = convertToDERCRequest(response.data);
          setInitialDERCRequest(dercRequest);
        } catch (error) {
          console.error('Error fetching DERC data:', error);
          setInitialDERCRequest(null);
        } finally {
          setLoading(false);
        }
      } else {
        setInitialDERCRequest(null);
      }
    };

    fetchDERCData();
  }, [open, dercId, asociaciones]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const isEditing = !!dercId;
  const isMode2 = defaultIdOrigenProducto === 2;
  const dialogTitle = title + (isEditing ? ' - Editar' : ' - Nuevo') + (isMode2 ? ' B' : '');

  return (
      <PopupDialog
        open={open}
        title={dialogTitle}
        onClose={handleClose}
        maxWidth={DERC_CONSTANTS.DIALOG_CONFIG.MAX_WIDTH}
        primaryAction={{
          label: isEditing ? 'Actualizar' : 'Guardar',
          onClick: handleSubmit,
          disabled: isSubmitting || loading || !isDirty,
          loading: isSubmitting,
          variant: 'contained',
          color: 'primary'
        }}
        secondaryAction={{
          label: 'Cancelar',
          onClick: handleClose,
          variant: 'outlined',
          color: 'inherit'
        }}
      >
        <Box sx={{ mt: 2 }}>
          {/* Form Type Title */}
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
              DERC{isMode2 && <span style={{ color: '#d32f2f' }}> B</span>}
            </Typography>
          </Box>

          {/* Mode 2 (Tipo B): fecha extraccion, especie, mallas and observacion */}
          {isMode2 ? (
            <>
              {/* Row 1 - Fecha Extraccion */}
              <Box sx={{ mb: 2 }}>
                <TextFieldComponent
                  label={DERC_CONSTANTS.FIELD_LABELS.fechaExtraccion}
                  type="date"
                  value={formData.fechaExtraccion}
                  onChange={(value) => handleFieldChange('fechaExtraccion', value)}
                  error={errors.fechaExtraccion}
                  required
                  fullWidth
                />
              </Box>

              {/* Row 2 - Número de Documento (Correlativo) */}
              <Box sx={{ mb: 2 }}>
                <DocumentNumberFieldB
                  label={DERC_CONSTANTS.FIELD_LABELS.numeroDocumento}
                  value={formData.numeroDocumento}
                  onChange={(value) => handleFieldChange('numeroDocumento', value)}
                  error={errors.numeroDocumento}
                  required
                  fechaExtraccion={formData.fechaExtraccion}
                  idOrigenProducto={defaultIdOrigenProducto}
                />
              </Box>

              {/* Row 3 - Especie y Es Entero */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr auto' }, gap: 2, mb: 2, alignItems: 'center' }}>
                <EspecieSelect
                  label={DERC_CONSTANTS.FIELD_LABELS.idEspecie}
                  value={formData.idEspecie}
                  onChange={(value: string | number) => handleFieldChange('idEspecie', value)}
                  error={errors.idEspecie}
                  required
                />
                <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                  <Typography variant="body2" color={!formData.isEntero ? 'primary' : 'text.secondary'} sx={{ fontWeight: !formData.isEntero ? 600 : 400 }}>
                    En Kilos
                  </Typography>
                  <Switch
                    checked={formData.isEntero}
                    onChange={(e) => handleFieldChange('isEntero', e.target.checked)}
                    color="primary"
                  />
                  <Typography variant="body2" color={formData.isEntero ? 'primary' : 'text.secondary'} sx={{ fontWeight: formData.isEntero ? 600 : 400 }}>
                    Entero
                  </Typography>
                </Box>
              </Box>

              {/* Row 4 - Numero Mallas */}
              <Box sx={{ mb: 2 }}>
                <TextFieldComponent
                  label={DERC_CONSTANTS.FIELD_LABELS.numeroMallas}
                  type="number"
                  value={formData.isEntero ? formData.numeroMallas : 0}
                  onChange={(value) => handleFieldChange('numeroMallas', value)}
                  error={formData.isEntero ? errors.numeroMallas : undefined}
                  required={formData.isEntero}
                  fullWidth
                  disabled={!formData.isEntero}
                />
              </Box>

              {/* Row 5 - Observacion */}
              <Box sx={{ mb: 2 }}>
                <TextFieldComponent
                  label={DERC_CONSTANTS.FIELD_LABELS.observacion}
                  value={formData.observacion}
                  onChange={(value) => handleFieldChange('observacion', value)}
                  error={errors.observacion}
                  fullWidth
                  placeholder="Observaciones adicionales (opcional)"
                  multiline
                  rows={3}
                />
              </Box>
            </>
          ) : (
            <>
              {/* Mode 1 (Tipo A): All fields */}
              {/* Row 1 - Fecha Extraccion y Placa Vehiculo */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                <TextFieldComponent
                  label={DERC_CONSTANTS.FIELD_LABELS.fechaExtraccion}
                  type="date"
                  value={formData.fechaExtraccion}
                  onChange={(value) => handleFieldChange('fechaExtraccion', value)}
                  error={errors.fechaExtraccion}
                  required
                  fullWidth
                />
                <TextFieldComponent
                  label={DERC_CONSTANTS.FIELD_LABELS.placaVehiculo}
                  value={formData.placaVehiculo}
                  onChange={(value) => handleFieldChange('placaVehiculo', value.toUpperCase())}
                  error={errors.placaVehiculo}
                  required
                  fullWidth
                  placeholder="Ej: P1W-832"
                />
              </Box>

              {/* Row 2 - Numero Documento y Asociación */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                <DocumentNumberField
                  label={DERC_CONSTANTS.FIELD_LABELS.numeroDocumento}
                  value={formData.numeroDocumento}
                  onChange={(value) => handleFieldChange('numeroDocumento', value)}
                  error={errors.numeroDocumento}
                  required
                  fechaExtraccion={formData.fechaExtraccion}
                />
                <AsociacionSelect
                  label={DERC_CONSTANTS.FIELD_LABELS.idAsociacion}
                  value={formData.idAsociacion}
                  onChange={(value: string | number) => handleFieldChange('idAsociacion', value)}
                  error={errors.idAsociacion}
                  required
                  showProcedencia={true}
                />
              </Box>

              {/* Row 3 - Especie y Es Entero */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr auto' }, gap: 2, mb: 2, alignItems: 'center' }}>
                <EspecieSelect
                  label={DERC_CONSTANTS.FIELD_LABELS.idEspecie}
                  value={formData.idEspecie}
                  onChange={(value: string | number) => handleFieldChange('idEspecie', value)}
                  error={errors.idEspecie}
                  required
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isEntero}
                      onChange={(e) => handleFieldChange('isEntero', e.target.checked)}
                      color="primary"
                    />
                  }
                  label={DERC_CONSTANTS.FIELD_LABELS.isEntero}
                  sx={{ ml: 1 }}
                />
              </Box>

              {/* Row 4 - Numero Mallas y Manojos */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2, mb: 2 }}>
                <TextFieldComponent
                  label={DERC_CONSTANTS.FIELD_LABELS.numeroMallas}
                  type="number"
                  value={formData.numeroMallas}
                  onChange={(value) => handleFieldChange('numeroMallas', value)}
                  error={errors.numeroMallas}
                  required
                  fullWidth
                />
                <TextFieldComponent
                  label={DERC_CONSTANTS.FIELD_LABELS.numeroManojos}
                  type="number"
                  value={formData.numeroManojos}
                  onChange={(value) => handleFieldChange('numeroManojos', value)}
                  error={errors.numeroManojos}
                  required
                  fullWidth
                />
              </Box>

              {/* Row 5 - Observación */}
              <Box sx={{ mb: 2 }}>
                <TextFieldComponent
                  label={DERC_CONSTANTS.FIELD_LABELS.observacion}
                  value={formData.observacion}
                  onChange={(value) => handleFieldChange('observacion', value)}
                  error={errors.observacion}
                  fullWidth
                  placeholder="Observaciones adicionales (opcional)"
                  multiline
                  rows={2}
                />
              </Box>
            </>
          )}
        </Box>
      </PopupDialog>
  );
};

export default EditDERC;